import Aura from '@primeng/themes/aura';
import { symBlue, symSurface } from '@/theme/sym70-preset';

/**
 * Catálogo de opciones del configurador de tema.
 *
 * Datos puros, sin dependencias de Angular ni acceso al DOM: este archivo
 * enumera QUÉ se puede elegir. Quien lo aplica es ThemeRuntimeService, y los
 * colores del cromo (sidebar/fondo) viven en `src/styles/brand/_schemes.scss`.
 * Para añadir o quitar una opción del drawer, este es el único archivo a tocar.
 */

/** Escala de color 50..950 tal como la esperan los presets de PrimeNG. */
export type ColorPalette = Record<string, string>;

/**
 * Paso de la escala que hace de "color de acción" (el que PrimeNG expone como
 * `primary.color` en modo claro, y el que la app consume como `--sym-blue-600`).
 *
 * La marca ancla en 600 (`#3B6FE8`, manual §2.3); Aura ancla en 500. Esa
 * diferencia de un escalón es la razón por la que el remapeo de `--sym-blue-*`
 * necesita saber el ancla en vez de copiar paso a paso — ver buildSymBlueMap().
 */
export type PrimaryAnchor = 500 | 600;

export interface PrimaryOption {
    id: string;
    label: string;
    /** Color del swatch en el drawer. `null` => se resuelve contra el texto (noir). */
    swatch: string | null;
    /** `null` solo para noir, que se define contra la superficie, no contra una escala. */
    palette: ColorPalette | null;
    anchor: PrimaryAnchor;
}

export interface SurfaceOption {
    id: string;
    label: string;
    palette: ColorPalette;
}

export interface SidebarSchemeOption {
    id: string;
    label: string;
    /**
     * El cromo del sidebar es oscuro con independencia del tema de la app, así
     * que el logo tiene que usar la variante negativa incluso en modo claro.
     */
    darkChrome: boolean;
    /** Muestra de color para el botón del drawer (CSS, puede ser un gradiente). */
    preview: string;
}

export interface BackgroundSchemeOption {
    id: string;
    label: string;
    preview: string;
}

/**
 * Cómo se dibuja la marca en el header del sidebar.
 *
 *  - `wordmark`: `logo-horizontal[-negativo].svg`. Cuál de las dos variantes se
 *    usa NO se elige aquí, lo decide el cromo del sidebar (ver `AppSidebar.logo()`).
 *  - `isotipo`: el símbolo suelto. Se pinta con la máscara `.sidebar-isotipo`,
 *    no como `<img>`, porque su cuadro más oscuro es `--sym-navy-900` y sobre el
 *    esquema Navy desaparecería (ver _schemes.scss).
 *  - `plate`: el símbolo sobre placa navy (`app-icon`). Trae su propio fondo, así
 *    que es el único que no necesita adaptarse al cromo.
 */
export type LogoVariantId = 'wordmark' | 'isotipo' | 'plate';

export interface LogoVariantOption {
    id: LogoVariantId;
    label: string;
    /** Miniatura del tile en el drawer. */
    preview: string;
    /**
     * Miniatura cuando el drawer está en oscuro. `null` => el mismo asset sirve
     * en ambos temas.
     *
     * Solo el logotipo la necesita: pinta su texto en `#141414`, así que sobre
     * el drawer oscuro las letras se perdían. El isotipo y la placa traen sus
     * propios azules y no dependen del fondo.
     */
    previewDark: string | null;
}

// ---------------------------------------------------------------------------
// Primary · marca
// ---------------------------------------------------------------------------

/**
 * Navy anclado en `--sym-navy-900` (#1B3A73). Escala derivada por extensión
 * coherente alrededor de ese valor, con el mismo criterio que `symBlue` en
 * sym70-preset.ts: el tono de marca cae en el paso 600 para que el remapeo sea
 * una identidad y no un desplazamiento.
 */
const symNavy: ColorPalette = {
    50: '#F1F4FA',
    100: '#E3E9F3',
    200: '#C4D0E4',
    300: '#9CADCE',
    400: '#6D84B2',
    500: '#425F93',
    600: '#1B3A73',
    700: '#163060',
    800: '#12274E',
    900: '#0D1D3B',
    950: '#091428'
};

/** Azul intenso anclado en `--sym-blue-700` (#2F55C9), mismo criterio. */
const symBlueDeep: ColorPalette = {
    50: '#F3F6FE',
    100: '#E7EDFD',
    200: '#C8D6F9',
    300: '#A2B8F1',
    400: '#7091E4',
    500: '#4A6FD6',
    600: '#2F55C9',
    700: '#2646A6',
    800: '#1E3883',
    900: '#182D68',
    950: '#111F49'
};

export const BRAND_PRIMARY_COLORS: PrimaryOption[] = [
    { id: 'brand', label: 'Azul de marca', swatch: symBlue[600], palette: symBlue, anchor: 600 },
    { id: 'brand-deep', label: 'Azul intenso', swatch: symBlueDeep[600], palette: symBlueDeep, anchor: 600 },
    { id: 'brand-navy', label: 'Navy', swatch: symNavy[600], palette: symNavy, anchor: 600 }
];

/** El default de la app: todo vuelve aquí con "Restablecer marca". */
export const DEFAULT_PRIMARY = 'brand-navy';

/**
 * El id cuyo palette es, literalmente, el que ya está escrito a mano en
 * `tokens.css`/`sym70-preset.ts` (symBlue). Es el único primary que no
 * necesita override de runtime: retirar el `<style>` en vez de reescribir sus
 * mismos valores.
 *
 * A propósito NO es lo mismo que `DEFAULT_PRIMARY`: ese es "qué ve el usuario
 * al entrar" y puede cambiar (ya cambió una vez, de 'brand' a 'brand-navy');
 * este es "qué paleta vive hardcodeada en el CSS estático", que no cambia sin
 * tocar tokens.css a mano. Confundir los dos hace que cambiar el default deje
 * de aplicar su propia paleta — pasó exactamente eso al mover `DEFAULT_PRIMARY`
 * a 'brand-navy' mientras este shortcut seguía comparando contra él.
 */
export const TOKENS_CSS_PRIMARY = 'brand';

// ---------------------------------------------------------------------------
// Primary · exploración (paleta del configurator de Atlantis)
// ---------------------------------------------------------------------------

const EXPLORATION_COLOR_NAMES = [
    'emerald',
    'green',
    'lime',
    'orange',
    'amber',
    'yellow',
    'teal',
    'cyan',
    'sky',
    'blue',
    'indigo',
    'violet',
    'purple',
    'fuchsia',
    'pink',
    'rose'
] as const;

/**
 * Las escalas se leen de las primitivas de Aura en vez de copiarse: son ~180
 * literales hex que ya vienen con la dependencia, y copiarlos los dejaría a la
 * deriva en la próxima subida de @primeng/themes.
 *
 * Se lee de Aura y no de Sym70Preset a propósito — el preset de marca
 * sobreescribe `primitive.blue` con la escala SyM70, y aquí interesa el `blue`
 * original de Aura como opción de comparación.
 */
const auraPrimitive = (Aura as unknown as { primitive: Record<string, ColorPalette> }).primitive;

export const EXPLORATION_PRIMARY_COLORS: PrimaryOption[] = [
    { id: 'noir', label: 'Noir', swatch: null, palette: null, anchor: 500 },
    ...EXPLORATION_COLOR_NAMES.map((name) => ({
        id: name,
        label: name.charAt(0).toUpperCase() + name.slice(1),
        swatch: auraPrimitive?.[name]?.['500'] ?? '#888888',
        palette: auraPrimitive?.[name] ?? null,
        anchor: 500 as PrimaryAnchor
    }))
];

export const ALL_PRIMARY_COLORS: PrimaryOption[] = [...BRAND_PRIMARY_COLORS, ...EXPLORATION_PRIMARY_COLORS];

export function findPrimary(id: string | null | undefined): PrimaryOption {
    return ALL_PRIMARY_COLORS.find((c) => c.id === id) ?? BRAND_PRIMARY_COLORS[0];
}

// ---------------------------------------------------------------------------
// Surface
// ---------------------------------------------------------------------------

/** El neutro de marca va primero: es el default y el único que respeta el DS. */
export const SURFACES: SurfaceOption[] = [
    { id: 'sym70', label: 'SyM70', palette: symSurface },
    {
        id: 'slate',
        label: 'Slate',
        palette: { 0: '#ffffff', 50: '#f8fafc', 100: '#f1f5f9', 200: '#e2e8f0', 300: '#cbd5e1', 400: '#94a3b8', 500: '#64748b', 600: '#475569', 700: '#334155', 800: '#1e293b', 900: '#0f172a', 950: '#020617' }
    },
    {
        id: 'gray',
        label: 'Gray',
        palette: { 0: '#ffffff', 50: '#f9fafb', 100: '#f3f4f6', 200: '#e5e7eb', 300: '#d1d5db', 400: '#9ca3af', 500: '#6b7280', 600: '#4b5563', 700: '#374151', 800: '#1f2937', 900: '#111827', 950: '#030712' }
    },
    {
        id: 'zinc',
        label: 'Zinc',
        palette: { 0: '#ffffff', 50: '#fafafa', 100: '#f4f4f5', 200: '#e4e4e7', 300: '#d4d4d8', 400: '#a1a1aa', 500: '#71717a', 600: '#52525b', 700: '#3f3f46', 800: '#27272a', 900: '#18181b', 950: '#09090b' }
    },
    {
        id: 'neutral',
        label: 'Neutral',
        palette: { 0: '#ffffff', 50: '#fafafa', 100: '#f5f5f5', 200: '#e5e5e5', 300: '#d4d4d4', 400: '#a3a3a3', 500: '#737373', 600: '#525252', 700: '#404040', 800: '#262626', 900: '#171717', 950: '#0a0a0a' }
    },
    {
        id: 'stone',
        label: 'Stone',
        palette: { 0: '#ffffff', 50: '#fafaf9', 100: '#f5f5f4', 200: '#e7e5e4', 300: '#d6d3d1', 400: '#a8a29e', 500: '#78716c', 600: '#57534e', 700: '#44403c', 800: '#292524', 900: '#1c1917', 950: '#0c0a09' }
    },
    {
        id: 'soho',
        label: 'Soho',
        palette: { 0: '#ffffff', 50: '#ececec', 100: '#dedfdf', 200: '#c4c4c6', 300: '#adaeb0', 400: '#97979b', 500: '#7f8084', 600: '#6a6b70', 700: '#55565b', 800: '#3f4046', 900: '#2c2c34', 950: '#16161d' }
    },
    {
        id: 'viva',
        label: 'Viva',
        palette: { 0: '#ffffff', 50: '#f3f3f3', 100: '#e7e7e8', 200: '#cfd0d0', 300: '#b7b8b9', 400: '#9fa1a1', 500: '#87898a', 600: '#6e7173', 700: '#565a5b', 800: '#3e4244', 900: '#262b2c', 950: '#0e1315' }
    },
    {
        id: 'ocean',
        label: 'Ocean',
        palette: { 0: '#ffffff', 50: '#fbfcfc', 100: '#F7F9F8', 200: '#EFF3F2', 300: '#DADEDD', 400: '#B1B7B6', 500: '#828787', 600: '#5F7274', 700: '#415B61', 800: '#29444E', 900: '#183240', 950: '#0c1920' }
    }
];

export const DEFAULT_SURFACE = 'sym70';

export function findSurface(id: string | null | undefined): SurfaceOption {
    return SURFACES.find((s) => s.id === id) ?? SURFACES[0];
}

// ---------------------------------------------------------------------------
// Esquemas de cromo · sidebar y fondo
// ---------------------------------------------------------------------------

export const SIDEBAR_SCHEMES: SidebarSchemeOption[] = [
    { id: 'surface', label: 'Surface', darkChrome: false, preview: 'var(--sym-surface)' },
    { id: 'light', label: 'Claro', darkChrome: false, preview: 'var(--sym-white)' },
    { id: 'navy', label: 'Navy', darkChrome: true, preview: 'var(--sym-navy-900)' },
    { id: 'gradient', label: 'Degradado', darkChrome: true, preview: 'linear-gradient(160deg, var(--sym-navy-950), var(--sym-blue-900))' },
    { id: 'primary', label: 'Primary', darkChrome: true, preview: 'var(--sym-blue-700)' }
];

export const DEFAULT_SIDEBAR_SCHEME = 'gradient';

export function findSidebarScheme(id: string | null | undefined): SidebarSchemeOption {
    return SIDEBAR_SCHEMES.find((s) => s.id === id) ?? SIDEBAR_SCHEMES[0];
}

export const BACKGROUND_SCHEMES: BackgroundSchemeOption[] = [
    { id: 'plain', label: 'Plano', preview: 'var(--sym-bg)' },
    { id: 'soft', label: 'Suave', preview: 'var(--sym-surface)' },
    { id: 'gradient', label: 'Degradado', preview: 'linear-gradient(160deg, var(--sym-surface), var(--sym-bg))' }
];

export const DEFAULT_BACKGROUND_SCHEME = 'gradient';

export function findBackgroundScheme(id: string | null | undefined): BackgroundSchemeOption {
    return BACKGROUND_SCHEMES.find((s) => s.id === id) ?? BACKGROUND_SCHEMES[0];
}

// ---------------------------------------------------------------------------
// Marca del sidebar
// ---------------------------------------------------------------------------

export const LOGO_VARIANTS: LogoVariantOption[] = [
    { id: 'wordmark', label: 'Logotipo', preview: '/images/brand/logo-horizontal.svg', previewDark: '/images/brand/logo-horizontal-negativo.svg' },
    { id: 'isotipo', label: 'Isotipo', preview: '/images/brand/isotipo.svg', previewDark: null },
    { id: 'plate', label: 'Placa', preview: '/images/brand/app-icon-512.png', previewDark: null }
];

export const DEFAULT_LOGO_VARIANT: LogoVariantId = 'isotipo';

export function findLogoVariant(id: string | null | undefined): LogoVariantOption {
    return LOGO_VARIANTS.find((l) => l.id === id) ?? LOGO_VARIANTS[0];
}

// ---------------------------------------------------------------------------
// Presets · combinaciones listas para usar
// ---------------------------------------------------------------------------

export interface ThemePresetOption {
    id: string;
    label: string;
    /** Descripción corta para el tile; qué se siente al usarlo, no qué setea. */
    hint: string;
    primary: string;
    surface: string;
    sidebarScheme: string;
    backgroundScheme: string;
    /**
     * Claro/oscuro forma parte del look, pero NO lo aplica ThemeRuntimeService
     * (eso vive en LayoutService, con su propia historia de bugs). El preset solo
     * declara la intención; quien orquesta las dos cosas es el configurador, que
     * es el único que tiene ambos servicios.
     */
    dark: boolean;
    /** Muestra "sidebar | contenido" con parada dura. */
    preview: string;
}

/**
 * `preview` no puede llevar un degradado como parada de color, así que los
 * esquemas de sidebar degradados se representan con su tono medio —el mismo
 * `--sym-blue-800` que usa `_schemes.scss` como base de mezcla—.
 *
 * Los tokens se resuelven contra el tema ACTIVO del drawer, no contra el del
 * preset, así que se eligen a propósito referencias que no se invierten en
 * oscuro (`--sym-navy-900`, `--sym-blue-800`, `--sym-white`): de lo contrario el
 * tile de "Nocturno" se dibujaría claro mientras la app está en claro.
 */
function splitPreview(chrome: string, page: string): string {
    return `linear-gradient(90deg, ${chrome} 0 38%, ${page} 38% 100%)`;
}

export const THEME_PRESETS: ThemePresetOption[] = [
    {
        id: 'sym70',
        label: 'SyM70',
        hint: 'La identidad de marca',
        primary: 'brand',
        surface: 'sym70',
        sidebarScheme: 'surface',
        backgroundScheme: 'plain',
        dark: false,
        preview: splitPreview('var(--sym-gray-100)', 'var(--sym-white)')
    },
    {
        id: 'ejecutivo',
        label: 'Ejecutivo',
        hint: 'Sidebar navy, lienzo gris',
        primary: 'brand',
        surface: 'sym70',
        sidebarScheme: 'navy',
        backgroundScheme: 'soft',
        dark: false,
        preview: splitPreview('var(--sym-navy-900)', 'var(--sym-gray-100)')
    },
    {
        id: 'estudio',
        label: 'Estudio',
        hint: 'Todo claro, mucho aire',
        primary: 'brand-deep',
        surface: 'sym70',
        sidebarScheme: 'light',
        backgroundScheme: 'soft',
        dark: false,
        preview: splitPreview('var(--sym-white)', 'var(--sym-gray-100)')
    },
    {
        id: 'horizonte',
        label: 'Horizonte',
        hint: 'Degradado de marca',
        primary: 'brand-deep',
        surface: 'sym70',
        sidebarScheme: 'gradient',
        backgroundScheme: 'gradient',
        dark: false,
        preview: splitPreview('var(--sym-blue-800)', 'var(--sym-gray-100)')
    },
    {
        id: 'nocturno',
        label: 'Nocturno',
        hint: 'Oscuro, para jornadas largas',
        primary: 'brand',
        surface: 'sym70',
        sidebarScheme: 'navy',
        backgroundScheme: 'plain',
        dark: true,
        preview: splitPreview('var(--sym-blue-800)', 'var(--sym-navy-900)')
    }
];

export function findThemePreset(id: string | null | undefined): ThemePresetOption | undefined {
    return THEME_PRESETS.find((p) => p.id === id);
}

// ---------------------------------------------------------------------------
// Remapeo de la escala --sym-blue-*
// ---------------------------------------------------------------------------

/**
 * Traduce la paleta elegida a los tokens `--sym-blue-*` / `--sym-navy-900` que
 * consume todo el SCSS propio de la app (sidebar, menú, tablas, badges).
 *
 * `updatePreset()` de PrimeNG solo mueve el preset; sin este remapeo, elegir
 * "emerald" dejaba los botones e inputs verdes y el sidebar todavía azul.
 *
 * El ancla es lo que hace que las opciones de marca sean una identidad: la
 * escala SyM70 pone su tono de acción en el paso 600, Aura en el 500, así que
 * para las opciones de exploración toda la rampa se desplaza un escalón.
 *
 * NO se tocan los semánticos (success/warning/danger) ni los grises: el
 * remapeo es de la escala azul y nada más.
 */
export function buildSymBlueMap(option: PrimaryOption): Record<string, string> | null {
    const palette = option.palette;
    if (!palette) return null; // noir se resuelve contra la superficie, no hay rampa que mapear

    // Pasos de la paleta que corresponden a cada token, de claro a oscuro.
    const steps = option.anchor === 600 ? ['100', '200', '300', '400', '500', '600', '700', '800', '900'] : ['50', '100', '200', '300', '400', '500', '600', '700', '900'];

    const tokens = ['--sym-blue-100', '--sym-blue-200', '--sym-blue-300', '--sym-blue-400', '--sym-blue-500', '--sym-blue-600', '--sym-blue-700', '--sym-blue-800', '--sym-navy-900'];

    const map: Record<string, string> = {};
    tokens.forEach((token, i) => {
        const value = palette[steps[i]];
        if (value) map[token] = value;
    });
    return map;
}
